import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { class: "kpi-section-header" }
const _hoisted_2 = { class: "kpi-section-content" }

import ColorLegend from '@/design-system/atoms/ColorLegend.vue'
import DashboardMissingFilter from './DashboardMissingFilter.vue'
import DashboardMissingData from './DashboardMissingData.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import { computed, inject, onUnmounted, toRefs, watch, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useKpiQueries, useKpis } from '../composables/useKpis'
import { Kpi, KpiValue, LocaleString } from '@/api-client/models/utilization/Contracts'
import useColoredEntities, { ColoredEntityData } from '@/composables/useColoredEntities'
import { ColorScaleConfig } from '@/utils/colorScale'
import { ANT_DESIGN_COLORS } from '@/utils/rgba'
import KPIQuery from './KPIQuery.vue'
import { sharedDataKey } from '../../types'
import { useRealm } from '@/composables/useRealm'
import { useFeature } from '@/pinia/features'
import { useCurrentFeatureFilters } from '@/composables/useFilters'

type Props = {
  title: LocaleString
  info?: LocaleString
  kpis: Kpi[]
  isMissingData: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'KPISection',
  props: {
    title: {},
    info: {},
    kpis: {},
    isMissingData: { type: Boolean }
  },
  setup(__props: any) {



const sharedData = inject(sharedDataKey)
const store = useStore()
const kpis = useKpis()
const filters = useCurrentFeatureFilters()
const { settings } = toRefs(useRealm())
const { locale, t } = useI18n()

const kpiInfo = computed(() => settings.value?.config?.utilization.kpiInformation)

const { selectedKpiType, selectedNumericValue, currentKpiSettings, selectedKpi } = toRefs(kpis)

const { kpiQueries } = useKpiQueries()

const { feature: colorLegend } = useFeature('home/analytics/analyticareas/usage/kpis/colorlegend')

const isColoredSpaceEnabled = computed(() => {
  return selectedKpiType.value !== undefined && store.getters.isPlanviewShowing
})
const isColoredBuildingsEnabled = computed(() => {
  if (!selectedKpiType.value) return false
  if (store.state.clickedItemType === 'building') return true
  if (store.state.clickedItemType !== 'complex' && store.state.clickedItemType !== 'realEstate')
    return false
  return true
})

const coloredEntitiesData = computed<ColoredEntityData[]>(() => {
  if (!selectedKpiType.value) return []
  return (
    (selectedKpi.value?.valuesBySplitBy ?? selectedKpi.value?.values)?.map(
      kpiValueToColoredEntityData
    ) ?? []
  )
})
// COLORED BUILDINGS
const coloredEntities = useColoredEntities()
const missingDataRooom = ref(false)

function missingDataRoom(missing: boolean) {
  missingDataRooom.value = missing
}
const submittedGeographicalFilters = computed(() => filters.value?.submittedGeographicalFilters)
watch(submittedGeographicalFilters, (room, newRoom) => {
  if (room !== newRoom) {
    missingDataRooom.value = false
  }
})
watch(
  coloredEntitiesData,
  () => {
    if (
      currentKpiSettings.value &&
      (isColoredBuildingsEnabled.value || isColoredSpaceEnabled.value)
    ) {
      coloredEntities.set({
        key: 'utilization',
        data: coloredEntitiesData.value,
        colorScale: currentKpiSettings.value?.colorScale ?? DEFAULT_COLOR_SCALE,
        title: currentKpiSettings.value?.title[locale.value] ?? '',
        unit: '%',
        formatter: (v) => Math.round(v * 100).toString()
      })
    } else {
      coloredEntities.reset()
    }
  },
  { immediate: true }
)
onUnmounted(() => coloredEntities.reset())

function kpiValueToColoredEntityData(kpiValue: KpiValue): ColoredEntityData {
  return {
    id: kpiValue.splitKey,
    meta: {
      numberOfSpaces: kpiValue.numberOfSpaces
    },
    value: kpiValue.value
  }
}

const DEFAULT_COLOR_SCALE: ColorScaleConfig = {
  type: 'INTERPOLATE',
  cutoffs: [
    {
      value: 0,
      color: ANT_DESIGN_COLORS['gray-2'].toHex()
    },
    {
      value: 0.5,
      color: ANT_DESIGN_COLORS['gray-7'].toHex()
    },
    {
      value: 1,
      color: ANT_DESIGN_COLORS['gray-12'].toHex()
    }
  ]
}

return (_ctx: any,_cache: any) => {
  return (!_unref(filters)?.submittedMeta.hasGeographicalFilter)
    ? (_openBlock(), _createBlock(DashboardMissingFilter, { key: 0 }))
    : (_ctx.isMissingData)
      ? (_openBlock(), _createBlock(DashboardMissingData, { key: 1 }))
      : (missingDataRooom.value)
        ? (_openBlock(), _createBlock(DashboardMissingData, { key: 2 }))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
            _createElementVNode("section", _hoisted_1, [
              _createVNode(InfoLabel, {
                title: _unref(t)('dashboard__text__measurement_values'),
                info: kpiInfo.value?.[_unref(locale)] ?? _unref(t)('dashboard__text__measurement_values-info'),
                size: 'mediumBold',
                fullscreen: _unref(sharedData)?.fullscreen ?? false
              }, null, 8, ["title", "info", "fullscreen"])
            ]),
            _createElementVNode("section", _hoisted_2, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(kpiQueries), (query) => {
                return (_openBlock(), _createBlock(KPIQuery, {
                  key: query.kpis.map((x) => x.kpi).join('_'),
                  query: query,
                  fullscreen: _unref(sharedData)?.fullscreen ?? false,
                  onMissingData: missingDataRoom
                }, null, 8, ["query", "fullscreen"]))
              }), 128)),
              (_unref(selectedKpiType) && !_unref(sharedData)?.fullscreen)
                ? (_openBlock(), _createBlock(ColorLegend, _mergeProps({
                    key: 0,
                    class: "legend",
                    title: _unref(currentKpiSettings)?.title[_unref(locale)],
                    colorScale: _unref(currentKpiSettings)?.colorScale ?? DEFAULT_COLOR_SCALE,
                    format: { format: { type: 'intl-number', style: 'percent', maximumFractionDigits: 0 } }
                  }, _unref(colorLegend)?.props, {
                    mean: _unref(selectedNumericValue) ?? 0
                  }), null, 16, ["title", "colorScale", "mean"]))
                : _createCommentVNode("", true)
            ])
          ], 64))
}
}

})